import React, { useContext, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../auth/ContextAuthentication';
import { ADMIN_CAPABILITIES } from '../auth/adminCapabilities';
import { FaUserAlt, FaRegFutbol, FaUserShield, FaCrown } from 'react-icons/fa';
import { TbSoccerField } from 'react-icons/tb';
import { ImHome } from 'react-icons/im';
import { BsLockFill } from 'react-icons/bs';
import { MdPassword } from 'react-icons/md';
import { IoIosArrowForward, IoIosArrowDown } from 'react-icons/io';
import PaymentIcon from '@mui/icons-material/Payment';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import { IoCreate } from 'react-icons/io5';
import { BiSolidDiscount } from 'react-icons/bi';
import { MaidanSidebar } from '../utils/svg/MaidanSidebar';

const SideNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState(false);
  const { clearSession, open, setOpen, hasCapability } = useContext(AuthContext);

  const hasAnyCapability = (requirements = []) => requirements.length === 0 || requirements.some(hasCapability);
  const handleLogout = () => { clearSession(); navigate('/login'); };
  const itemClass = (path) => `flex items-center cursor-pointer p-2 space-x-3 rounded-md ${location.pathname === path ? 'bg-white bg-opacity-20' : ''}`;

  const menuItems = [
    {path: '/', name: 'Dashboard', Icon: () => <ImHome color="white" style={{fontSize: '24px'}} />, capabilities: []},
    {path: '/administrators', name: 'Administrators', Icon: () => <FaUserShield color="white" style={{fontSize: '24px'}} />, capabilities: [ADMIN_CAPABILITIES.PROVISION]},
    {path: '/playersprofile', name: 'Users', Icon: () => <FaUserAlt color="white" style={{fontSize: '24px'}} />, capabilities: [ADMIN_CAPABILITIES.USERS_MANAGE]},
    {path: '/matchprofile', name: 'Matches', Icon: () => <FaRegFutbol color="white" style={{fontSize: '24px'}} />, capabilities: [ADMIN_CAPABILITIES.MATCHES_MANAGE]},
    {path: '/groundprofile', name: 'Grounds', Icon: () => <TbSoccerField color="white" style={{fontSize: '28px'}} />, capabilities: [ADMIN_CAPABILITIES.GROUNDS_MANAGE]},
    {path: '/groundsettlement', name: 'Grounds Settlements', Icon: () => <TbSoccerField color="white" style={{fontSize: '28px'}} />, capabilities: [ADMIN_CAPABILITIES.PAYMENTS_READ]},
    {path: '/sportsbookings', name: 'Sport Bookings', Icon: () => <AcUnitIcon style={{fontSize: '24px', color: 'white'}} />, capabilities: [ADMIN_CAPABILITIES.SPORTS_MANAGE, ADMIN_CAPABILITIES.EXPORTS_READ]},
    {path: '/academies', name: 'Academies', Icon: () => <AcUnitIcon style={{fontSize: '24px', color: 'white'}} />, capabilities: [ADMIN_CAPABILITIES.ACADEMIES_MANAGE]},
    {path: '/receipts', name: 'Receipts', Icon: () => <ReceiptLongIcon style={{fontSize: '24px', color: 'white'}} />, capabilities: [ADMIN_CAPABILITIES.PAYMENTS_READ]},
    {path: '/paymentverification', name: 'Payment Verification', Icon: () => <PaymentIcon style={{fontSize: '24px', color: 'white'}} />, capabilities: [ADMIN_CAPABILITIES.PAYMENTS_MANAGE]},
    {path: '/premium', name: 'Premium', Icon: () => <FaCrown color="#EDD607" style={{fontSize: '24px'}} />, capabilities: [ADMIN_CAPABILITIES.PREMIUM_READ, ADMIN_CAPABILITIES.PREMIUM_REPORTS_READ]},
  ];

  const visibleItems = menuItems.filter((item) => hasAnyCapability(item.capabilities));
  const canManagePromotions = hasCapability(ADMIN_CAPABILITIES.PROMOTIONS_MANAGE);

  return (
    <div className={`${open ? 'w-20' : 'w-60'} fixed left-0 top-0 h-screen bg-[#0C7240] p-3 duration-300 z-20 overflow-y-auto`}>
      <div className="space-y-3 flex justify-center flex-col min-h-full">
        <div className="flex items-center justify-between">
          <MaidanSidebar />
          <button onClick={() => setOpen(!open)} aria-label="Toggle navigation">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h8m-8 6h16" />
            </svg>
          </button>
        </div>

        <div className="flex-1">
          <ul className="pt-2 pb-4 space-y-1">
            {visibleItems.map(({path, name, Icon}) => (
              <li key={path} className="rounded-sm">
                <button className={`${itemClass(path)} w-full text-left`} onClick={() => navigate(path)}>
                  <Icon />
                  <span className="text-gray-100 text-lg">{!open ? name : ''}</span>
                </button>
              </li>
            ))}

            {canManagePromotions && (
              <li className="rounded-sm">
                <button className="flex items-center cursor-pointer p-2 space-x-3 rounded-md w-full text-left" onClick={() => setOpenMenu((value) => !value)}>
                  {openMenu ? <IoIosArrowDown style={{fontSize: '24px', color: 'white'}} /> : <IoIosArrowForward style={{fontSize: '24px', color: 'white'}} />}
                  <span className="text-gray-100 text-lg">{!open ? 'Promotions' : ''}</span>
                </button>
                {openMenu && (
                  <div className={`${!open ? 'ml-6' : ''} mt-2 space-y-2`}>
                    <button className={`${itemClass('/promotions/create')} w-full text-left`} onClick={() => navigate('/promotions/create')}>
                      <IoCreate style={{fontSize: '24px', color: 'white'}} />
                      <span className="text-gray-100 text-lg">{!open ? 'Create' : ''}</span>
                    </button>
                    <button className={`${itemClass('/promotions/view-all')} w-full text-left`} onClick={() => navigate('/promotions/view-all')}>
                      <BiSolidDiscount style={{fontSize: '24px', color: 'white'}} />
                      <span className="text-gray-100 text-lg">{!open ? 'View All' : ''}</span>
                    </button>
                  </div>
                )}
              </li>
            )}
          </ul>
        </div>

        <ul className="pt-2 pb-4 space-y-1">
          <li className="rounded-sm">
            <button className={`${itemClass('/updatepassword')} w-full text-left`} onClick={() => navigate('/updatepassword')}>
              <MdPassword color="white" style={{fontSize: '24px'}} />
              <span className="text-gray-100 text-lg">{!open ? 'Change Password' : ''}</span>
            </button>
          </li>
          <li className="rounded-sm">
            <button className="flex items-center cursor-pointer p-2 space-x-3 rounded-md w-full text-left" onClick={handleLogout}>
              <BsLockFill color="white" style={{fontSize: '24px'}} />
              <span className="text-gray-100 text-lg">{!open ? 'Logout' : ''}</span>
            </button>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default SideNav;
